import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import NameAndId from '@src/components/NameAndId.vue'
import UpdateOrCreatePlatformMembershipDocumentForm from '@src/partials/platforms/UpdateOrCreatePlatformMembershipDocumentForm.vue'
import { PlatformAttachment, PlatformMembershipDocument } from '@src/types'
import { uploadPrivate } from '@src/utils/api/s3'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useUserStore } from '@src/utils/stores/user'
import { computed, h, ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function usePlatformMembershipDocumentsTable(
    membershipDocuments: Ref<PlatformMembershipDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const {
        TableWrapper: PlatformMembershipDocumentsTable,
        selected: selectedPlatformMembershipDocumentsIds,
    } = useTable({
        tableProps: {
            id: 'platform_membership_documents',
            label: 'platform_membership_documents',
            help: 'help_platform_membership_documents_table',
            data: membershipDocuments,
            total: membershipDocuments.value.length,
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformAttachment }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewPlatformMembershipDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleUpdateOrCreatePlatformMembershipDocument,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('update_platform'),
                },
                {
                    action: handleDeletePlatformMembershipDocument,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_platform'),
                },
            ],
        },
    })

    function handleViewPlatformMembershipDocument(
        platformMembershipDocument: PlatformMembershipDocument
    ) {
        const url = platformMembershipDocument.url
        if (url) {
            window.open(url)
        }
    }

    function handleUpdateOrCreatePlatformMembershipDocument(
        platformMembershipDocument: PlatformMembershipDocument = new PlatformMembershipDocument()
    ) {
        const editedPlatformMembershipDocument = ref(
            new PlatformMembershipDocument()
        )
        editedPlatformMembershipDocument.value = {
            ...platformMembershipDocument,
        }
        const file = ref<File | null>(null)
        isModalOpen.value = true

        const disableCreate = computed(() => {
            return !(
                editedPlatformMembershipDocument.value.upload_key &&
                editedPlatformMembershipDocument.value.language
            )
        })
        openModal(
            {
                id: 'edit_platform_attachment',
                title: editedPlatformMembershipDocument.value.id
                    ? 'modal_title_update_platform_membership_document'
                    : 'modal_title_create_platform_membership_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    savePlatformMembershipDocument(
                        editedPlatformMembershipDocument.value,
                        file.value as File
                    ),
            },
            () =>
                h(UpdateOrCreatePlatformMembershipDocumentForm, {
                    // eslint-disable-next-line quote-props
                    modelValue: editedPlatformMembershipDocument,
                    'onUpdate:modelValue': (
                        value: unknown,
                        files: Ref<FileList>
                    ) => {
                        file.value = files.value?.item(0)
                    },
                }),
            {
                submitButtonText: editedPlatformMembershipDocument.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    async function savePlatformMembershipDocument(
        platformMembershipDocument: PlatformMembershipDocument,
        file: File
    ) {
        const membershipDocumentsTemp = [...membershipDocuments.value]
        if (file) {
            const uploadPrivateResponse = await uploadPrivate(file)
            platformMembershipDocument.upload_key =
                uploadPrivateResponse.data.key
            platformMembershipDocument.url =
                uploadPrivateResponse.data.temp_storage_url
        }

        if (platformMembershipDocument.id) {
            membershipDocuments.value[
                membershipDocuments.value.findIndex(
                    (membershipDocument: PlatformMembershipDocument) =>
                        platformMembershipDocument.id == membershipDocument.id
                )
            ] = platformMembershipDocument
        } else {
            membershipDocuments.value.push(platformMembershipDocument)
        }

        let toast = 'toast_success_platform_membership_document_updated'
        if (!platformMembershipDocument.id) {
            toast = 'toast_success_platform_membership_document_created'
        }
        update(
            i18n.t(toast, {
                attachment: platformMembershipDocument.name,
            })
        )
            .catch((error) => {
                membershipDocuments.value = membershipDocumentsTemp
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    function handleDeletePlatformMembershipDocument(
        platformMembershipDocument: PlatformMembershipDocument
    ) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_membership_document',
                title: 'modal_title_delete_platform_membership_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    deletePlatformMembershipDocument(
                        platformMembershipDocument
                    ),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [platformMembershipDocument],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformMembershipDocument(
        platformMembershipDocumentToDelete: PlatformMembershipDocument,
        submit = true
    ) {
        const membershipDocumentsTemp = [...membershipDocuments.value]
        membershipDocuments.value.splice(
            membershipDocuments.value.findIndex(
                (platformMembershipDocument) =>
                    platformMembershipDocumentToDelete.id ==
                    platformMembershipDocument.id
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_platform_membership_document_deleted', {
                    platformMembershipDocument:
                        platformMembershipDocumentToDelete.name,
                })
            )
                .catch((error) => {
                    membershipDocuments.value = membershipDocumentsTemp
                    throw error
                })
                .then(() => {
                    selectedPlatformMembershipDocumentsIds.value = []
                    isModalOpen.value = false
                })
        }
    }

    function handleDeletePlatformMembershipDocuments() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_membership_documents',
                title: 'modal_title_delete_platform_membership_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformMembershipDocuments(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedPlatformMembershipDocuments.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformMembershipDocuments() {
        const membershipDocumentsTemp = [...membershipDocuments.value]
        selectedPlatformMembershipDocuments.value.forEach(
            (platformMembershipDocument) => {
                deletePlatformMembershipDocument(
                    platformMembershipDocument,
                    false
                )
            }
        )

        update(
            i18n.t('toast_success_platform_membership_documents_deleted', {
                membershipDocuments: selectedPlatformMembershipDocuments.value
                    .map(
                        (platformMembershipDocument) =>
                            platformMembershipDocument.name
                    )
                    .join(', '),
            })
        )
            .catch((error) => {
                membershipDocuments.value = membershipDocumentsTemp
                throw error
            })
            .then(() => {
                selectedPlatformMembershipDocumentsIds.value = []
                isModalOpen.value = false
            })
    }

    const selectedPlatformMembershipDocuments = computed(() => {
        return membershipDocuments.value.filter(
            (item: PlatformMembershipDocument) =>
                item.id &&
                selectedPlatformMembershipDocumentsIds.value.includes(item.id)
        )
    })

    return {
        PlatformMembershipDocumentsTable,
        selectedPlatformMembershipDocumentsIds,
        handleUpdateOrCreatePlatformMembershipDocument,
        handleDeletePlatformMembershipDocuments,
    }
}
